import { memo, useCallback, useMemo, useEffect, useState } from "react";
import {
  Globe,
  Swords,
  Target,
  FlagTriangleRight,
  ArrowRight,
  Activity,
  BarChart3,
  ChevronRight,
  Circle,
  Trophy
} from "lucide-react";
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { sportsHubPtBR } from '../mappings/sportsHub.pt-br';
import SEOHead from '../SEO/SEOHead';
import UnifiedNavHeader from '../navigation/UnifiedNavHeader';
import { useNavigate } from 'react-router-dom';

const getDashboardsData = (t) => [
  {
    id: "football",
    title: t("Football"),
    subtitle: t("Soccer"),
    description: t("Live fixtures, standings, and player statistics from top leagues worldwide."),
    icon: Globe,
    path: "/football",
    features: [t("Premier League"), t("La Liga"), t("Champions League"), t("Live Scores")],
    stats: { [t("matches")]: "2.5K+", [t("players")]: "50K+" },
    status: "active"
  },
  {
    id: "f1",
    title: t("F1"),
    subtitle: t("Formula 1"),
    description: t("Formula 1 race schedules, driver standings, and team information."),
    icon: Activity,
    path: "/f1",
    features: [t("Race Calendar"), t("Driver Standings"), t("Team Stats"), t("Circuit Info")],
    stats: { [t("races")]: "24+", [t("drivers")]: "20" },
    status: "active"
  },
  {
    id: "mlb",
    title: t("MLB + International"),
    subtitle: t("Baseball"),
    description: t("Live games, standings, EV calculator, and comprehensive baseball statistics."),
    icon: BarChart3,
    path: "/mlb",
    features: [t("Live Scores"), t("Standings"), t("EV Calculator"), t("Real Odds Data")],
    stats: { [t("leagues")]: "77+", [t("teams")]: "600+" },
    status: "active"
  },
  {
    id: "mma",
    title: t("MMA Fights"),
    subtitle: t("Mixed Martial Arts"),
    description: t("Fight schedules, fighter stats, and real-time match updates."),
    icon: Swords,
    path: "/mma-fights",
    features: [t("UFC Events"), t("Fighter Stats"), t("Fight Results"), t("Live Updates")],
    stats: { [t("events")]: "300+", [t("fighters")]: "5K+" },
    status: "active"
  },
  {
    id: "nfl",
    title: t("NFL"),
    subtitle: t("American Football"),
    description: t("American Football games, team standings, and player statistics."),
    icon: FlagTriangleRight,
    path: "/nfl",
    features: [t("Game Schedules"), t("Team Stats"), t("Player Rosters"), t("Standings")],
    stats: { [t("teams")]: "32", [t("players")]: "1.7K+" },
    status: "active"
  },
  {
    id: "ncaa-football",
    title: t("NCAAF"),
    subtitle: t("College Football"),
    description: t("American College Football games, team standings, and player statistics."),
    icon: FlagTriangleRight,
    path: "/ncaaf",
    features: [t("Game Schedules"), t("Team Stats"), t("Player Rosters"), t("Standings")],
    stats: { [t("teams")]: "130+", [t("conferences")]: t("10") },
    status: "active"
  },
  {
    id: "volleyball",
    title: t("Volleyball"),
    subtitle: t("International Leagues"),
    description: t("International volleyball matches, league tables, and team information."),
    icon: Target,
    path: "/volleyball",
    features: [t("Live Matches"), t("League Tables"), t("Team Stats"), t("Tournaments")],
    stats: { [t("leagues")]: "20+", [t("teams")]: "500+" },
    status: "active"
  },
  {
    id: "nba",
    title: t("NBA"),
    subtitle: t("Basketball"),
    description: t("National Basketball Association games, standings, and player statistics."),
    icon: Circle,
    path: "/nba",
    features: [t("Live Games"), t("Player Stats"), t("Team Standings"), t("Playoffs")],
    stats: { [t("teams")]: "30", [t("players")]: "450+" },
    status: "active"
  },
  {
    id: "wnba",
    title: t("WNBA"),
    subtitle: t("Women's Basketball"),
    description: t("Women's National Basketball Association games and statistics."),
    icon: Circle,
    path: "/wnba",
    features: [t("Live Games"), t("Player Stats"), t("Team Standings"), t("Championships")],
    stats: { [t("teams")]: "12", [t("players")]: "144+" },
    status: "coming-soon"
  },
  {
    id: "nhl",
    title: t("NHL"),
    subtitle: t("Hockey"),
    description: t("National Hockey League games, standings, and player statistics."),
    icon: Trophy,
    path: "/nhl",
    features: [t("Live Games"), t("Player Stats"), t("Team Standings"), t("Stanley Cup")],
    stats: { [t("teams")]: "32", [t("players")]: "700+" },
    status: "active"
  }
];

// --- Components ---

const FeatureList = memo(({ features }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2">
    {features.map((feature, i) => (
      <div key={i} className="flex items-center gap-1.5">
        <div className="flex-shrink-0">
          <span className="text-white/20 text-[8px]">▪</span>
        </div>
        <span className="text-white/30 text-[10px] sm:text-[11px] font-mono">{feature}</span>
      </div>
    ))}
  </div>
));

FeatureList.displayName = "FeatureList";

const StatusBadge = memo(({ status }) => {
  if (status === "active") {
    return (
      <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex items-center gap-1 bg-gradient-to-r from-emerald-500/[0.08] to-violet-500/[0.08] border border-emerald-500/20 px-2 py-0.5 rounded">
        <div className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse" />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-violet-400 text-[8px] sm:text-[9px] font-mono uppercase tracking-wider">LIVE</span>
      </div>
    );
  }

  return (
    <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex items-center gap-1 bg-white/[0.02] border border-white/[0.03] px-2 py-0.5 rounded">
      <span className="text-white/20 text-[8px] sm:text-[9px] font-mono uppercase tracking-wider">SOON</span>
    </div>
  );
});

StatusBadge.displayName = "StatusBadge";

const DashboardCard = memo(({ dashboard, onNavigate, t }) => {
  const Icon = dashboard.icon;
  const statKey = Object.keys(dashboard.stats)[0];
  const statValue = dashboard.stats[statKey];
  const isActive = dashboard.status === "active";

  return (
    <div className={`relative backdrop-blur-sm rounded-lg p-4 sm:p-5 border transition-all group ${
      isActive
        ? "bg-gradient-to-r from-emerald-500/[0.03] to-violet-500/[0.03] border-emerald-500/10 hover:border-violet-500/20"
        : "bg-white/[0.02] border-white/[0.03] hover:border-white/[0.05]"
    } hover:bg-white/[0.03]`}>
      <StatusBadge status={dashboard.status} />
      
      <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-4 sm:mb-6">
        <div className="flex-shrink-0">
          <div className={`p-2 rounded border ${
            isActive
              ? "bg-gradient-to-r from-emerald-500/[0.08] to-violet-500/[0.08] border-emerald-500/20"
              : "bg-white/[0.03] border-white/[0.05]"
          }`}>
            <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${
              isActive
                ? "text-emerald-400/70"
                : "text-white/30"
            } transition-colors`} />
          </div>
        </div>
        <div className="flex-grow">
          <h3 className={`font-medium text-[12px] sm:text-[13px] mb-0.5 uppercase tracking-[0.15em] ${
            isActive
              ? "text-white/90"
              : "text-white/70"
          }`}>
            {dashboard.title}
          </h3>
          <p className="text-white/30 text-[9px] sm:text-[10px] font-mono">{dashboard.subtitle}</p>
        </div>
        <div className="text-right absolute top-10 right-4 sm:relative sm:top-0 sm:right-0">
          <div className={`text-sm sm:text-base font-mono ${
            isActive ? "text-violet-400/70" : "text-white/40"
          }`}>
            {statValue}
          </div>
          <div className="text-[8px] sm:text-[9px] text-white/20 uppercase font-mono">{statKey}</div>
        </div>
      </div>
      
      <p className="text-white/40 leading-relaxed mb-3 sm:mb-4 text-[10px] sm:text-[11px] font-mono">
        {dashboard.description}
      </p>
      
      <div className="mb-6 sm:mb-8">
        <FeatureList features={dashboard.features} />
      </div>
      
      <button
        onClick={() => {
          if (dashboard.status === 'coming-soon') {
            onNavigate('/chat');
          } else {
            onNavigate(dashboard.path);
          }
        }}
        className={`px-4 sm:px-6 py-2 sm:py-2.5 border rounded-lg transition-all flex items-center justify-center text-[10px] sm:text-[11px] w-full font-mono uppercase tracking-wider group ${
          isActive
            ? "bg-gradient-to-r from-emerald-500/[0.12] to-violet-500/[0.12] border-emerald-500/30 hover:border-violet-500/40 text-white/90 font-semibold"
            : "bg-white/[0.02] border-white/[0.05] text-white/30 hover:text-white/50 hover:bg-white/[0.03]"
        }`}
      >
        {isActive ? t("ACCESS QUICKPICKS") : t("ONLY AI CHAT MODE")}
        <ArrowRight className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ml-1.5 transition-transform group-hover:translate-x-0.5 ${
          isActive ? "text-violet-400/60" : "text-white/30"
        }`} />
      </button>
    </div>
  );
});

DashboardCard.displayName = "DashboardCard";

// --- Main Hub Component ---

const SportsHub = memo(({ navigate: propNavigate }) => {
  const navigate = useNavigate() || propNavigate;
  const isBrazilian = getBrazilianStatus();
  
  // Helper function to get translated text
  const t = useCallback((key) => {
    return isBrazilian ? (sportsHubPtBR[key] || key) : key;
  }, [isBrazilian]);

  const handleNavigate = useCallback(
    path => {
      if (navigate) {
        navigate(path);
      } else {
        // For non-React Router setups, use window.location
        window.location.href = path;
      }
    },
    [navigate]
  );



  const dashboardCards = useMemo(
    () =>
      getDashboardsData(t).map(dashboard => (
        <DashboardCard key={dashboard.id} dashboard={dashboard} onNavigate={handleNavigate} t={t} />
      )),
    [handleNavigate, t]
  );

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // No particle animation - clean terminal aesthetic

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-black/95 overflow-hidden">
        {/* Desktop Navigation Header */}
        <div className="hidden md:block">
          <UnifiedNavHeader 
            title={t("Sports Hub")}
            showBackButton={true}
          />
        </div>
        
        {/* Mobile Header - Bigger and QuickPicks focused */}
        <div className="md:hidden bg-black/90 backdrop-blur-lg border-b border-white/[0.05] sticky top-0 z-40">
          <div className="px-3 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-gradient-to-r from-emerald-500/[0.08] to-violet-500/[0.08] rounded border border-emerald-500/20">
                <Activity className="text-emerald-400/60" size={18} />
              </div>
              <div>
                <h1 className="text-[15px] font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-violet-400 uppercase tracking-[0.15em]">{t("QuickPicks")}</h1>
                <p className="text-[10px] text-white/30 font-mono">{t("AI picks for all games")}</p>
              </div>
            </div>
            
            {/* Mobile Breadcrumbs - Terminal style */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigate('/home')}
                className="px-2 py-0.5 bg-white/[0.02] hover:bg-white/[0.03] rounded text-white/30 hover:text-white/50 text-[9px] font-mono uppercase tracking-wider transition-all border border-white/[0.03]"
              >
                HOME
              </button>
              <ChevronRight className="w-3 h-3 text-white/20" />
              <div className="px-2 py-0.5 bg-violet-500/[0.08] rounded text-violet-400/60 text-[9px] font-mono uppercase tracking-wider border border-violet-500/20">
                SPORTS
              </div>
            </div>
          </div>
        </div>
        
      
      {/* Main content - Adjusted padding for header */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-4 sm:py-8 md:py-12">
        
        {/* Hero section - The Pick Lab */}
        <div className="text-center mb-4 sm:mb-6">
          {/* Main Title - Bigger */}
          <h1 className="text-[20px] sm:text-[26px] md:text-[32px] font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-violet-400 uppercase tracking-[0.2em] mb-2">
            {t("The Pick Lab")}
          </h1>

          {/* Comprehensive Subtitle */}
          <div className="max-w-3xl mx-auto space-y-1.5 px-4">
            <p className="text-[11px] sm:text-[12px] text-white/50 font-mono">
              {t("Access every game - live or future. View standings, league fixtures, and player data.")}
            </p>
            <p className="text-[10px] sm:text-[11px] text-white/30 font-mono">
              {t("QuickPicks provide AI confidence ratings for straight bets. Enhanced insights coming soon for maximum speed-to-decision.")}
              {' '}
              <button
                onClick={() => handleNavigate('/market')}
                className="inline-flex items-center text-violet-400 hover:text-violet-300 transition-colors font-semibold"
              >
                {t("See community consensus")}
                <ArrowRight className="w-2.5 h-2.5 ml-0.5" />
              </button>
            </p>
          </div>
        </div>

        {/* Quick Links - DraftKings style */}
        <div className="mb-8 px-4">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-[10px] text-white/30 uppercase tracking-[0.2em] mb-3 font-medium">QUICK LINKS</h3>
            <div className="flex flex-wrap gap-2">
              {getDashboardsData(t).map((sport) => {
                const isActive = sport.status === 'active';
                const isComingSoon = sport.status === 'coming-soon';

                return (
                  <button
                    key={sport.id}
                    onClick={() => {
                      if (isComingSoon) {
                        handleNavigate('/chat');
                      } else {
                        handleNavigate(sport.path);
                      }
                    }}
                    disabled={!isActive && !isComingSoon}
                    style={{ transform: 'skewX(-8deg)' }}
                    className={`border rounded px-4 py-2 md:px-5 md:py-2.5 transition-all text-xs md:text-sm font-medium
                      ${isActive
                        ? 'border-white/20 bg-white/5 text-white/90 hover:bg-white/10 hover:border-white/30'
                        : isComingSoon
                        ? 'border-white/10 bg-white/[0.02] text-white/50 hover:bg-white/5'
                        : 'border-white/5 text-white/20 cursor-not-allowed opacity-50'}`}
                  >
                    <span style={{ transform: 'skewX(8deg)', display: 'inline-block' }}>
                      {sport.id === 'football' ? '100+ Futebol Leagues' :
                       sport.id === 'f1' ? 'F1' :
                       sport.id === 'mlb' ? 'Baseball' :
                       sport.id === 'ncaa-football' ? 'NCAA Football' :
                       sport.id === 'mma' ? 'MMA' :
                       sport.id === 'nfl' ? 'NFL' :
                       sport.id === 'nba' ? 'NBA' :
                       sport.id === 'volleyball' ? 'Volleyball' :
                       sport.id === 'wnba' ? 'WNBA' :
                       sport.id === 'nhl' ? 'NHL' :
                       sport.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Beta banner - Terminal style */}
        <div className="mb-4 sm:mb-6">
          <div className="bg-white/[0.02] border border-white/[0.05] rounded p-2 sm:p-2.5">
            <p className="text-[9px] sm:text-[10px] text-white/30 text-center font-mono uppercase tracking-wider">
              {t("V3 BETA • LIMITED FEATURES • EXPANDING SOON")}
            </p>
          </div>
        </div>

        {/* Sports Dashboards - Reduced top spacing on mobile */}
        <div className="mb-8 sm:mb-12 md:mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6 md:gap-8">
            {dashboardCards}
          </div>
        </div>

        {/* Coming Soon section - Terminal style */}
        <div className="bg-white/[0.02] backdrop-blur-sm rounded-lg p-6 sm:p-8 text-center border border-white/[0.05] hover:border-white/[0.08] transition-all">
          <h2 className="text-[12px] sm:text-[13px] font-medium text-white/30 uppercase tracking-[0.25em] mb-3">
            {t("MORE SPORTS COMING SOON")}
          </h2>

          <p className="text-white/20 mb-4 sm:mb-6 max-w-2xl mx-auto text-[10px] sm:text-[11px] font-mono px-4">
            {t("We're constantly expanding our coverage. Basketball, hockey, tennis, and more advanced analytics features are on the way.")}
          </p>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
            <button
              onClick={() => handleNavigate('/pricing')}
              className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-violet-500/[0.08] to-violet-600/[0.08] border border-violet-500/20 hover:border-violet-400/30 text-violet-400/70 hover:text-violet-400/90 font-mono rounded transition-all text-[10px] sm:text-[11px] uppercase tracking-wider flex items-center justify-center"
            >
              {t("GET PRO ACCESS")}
              <ArrowRight className="w-3 h-3 ml-1.5" />
            </button>
            <button
              onClick={() => handleNavigate('/chat')}
              className="px-4 sm:px-6 py-2 sm:py-2.5 bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08] text-white/30 hover:text-white/50 font-mono rounded transition-all text-[10px] sm:text-[11px] uppercase tracking-wider"
            >
              {t("ASK AI")}
            </button>
          </div>
        </div>
      </div>
      
      {/* Removed Coming Soon Toast - no longer needed */}
    </div>
    </>
  );
});

SportsHub.displayName = "SportsHub";

export default SportsHub;